import type { MisconceptionDefinition } from './types'

/** SWW — Spelling werkwoorden misconcepties. */
export const SWW_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'sww.kofschip-misapplied',
    label: 'Kofschip verkeerd toegepast',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel verkeerd toe bij voltooid deelwoord.',
    typicalFamilies: ['SWW-VD-KOF', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.stem-extraction-error',
    label: 'Stam verkeerd bepaald',
    module: 'SpellingWW',
    description:
      'Leerling bepaalt de stam van het werkwoord verkeerd (bijv. "leeft" → stam "leef" i.p.v. "leev").',
    typicalFamilies: ['SWW-TT-STAM', 'SWW-TT-DSTAM'],
  },
  {
    id: 'sww.past-participle-ge-omitted',
    label: 'Ge- weggelaten',
    module: 'SpellingWW',
    description:
      'Leerling vergeet ge- bij het voltooid deelwoord (of voegt het onterecht toe).',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.irregular-verb-regularized',
    label: 'Onregelmatig werkwoord vervoegd als regelmatig',
    module: 'SpellingWW',
    description:
      'Leerling past regelmatige vervoegingsregels toe op een onregelmatig werkwoord.',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VD-STERK'],
  },
  // === Leenwoorden (Engels / Frans) ===
  {
    id: 'sww.loanword-spelling-stripped',
    label: 'Engelse/Franse spelling uit stam weggelaten',
    module: 'SpellingWW',
    description:
      'Leerling verwijdert delen van de oorspronkelijke spelling (bv. eind-"e", dubbele letter) van de stam van een leenwoord.',
    typicalFamilies: [
      'SWW-LEEN-ENG-STAM',
      'SWW-LEEN-ENG-KOF',
      'SWW-LEEN-FRANS',
    ],
  },
  {
    id: 'sww.loanword-vowel-doubled',
    label: 'Klinker verdubbeld volgens Nederlandse regel',
    module: 'SpellingWW',
    description:
      'Leerling verdubbelt een klinker in de stam van een leenwoord volgens Nederlandse open/gesloten lettergreepregels, terwijl de leenwoordspelling behouden moet blijven.',
    typicalFamilies: ['SWW-LEEN-ENG-STAM'],
  },
  {
    id: 'sww.stem-with-extra-letter',
    label: 'Extra letter toegevoegd aan stam',
    module: 'SpellingWW',
    description:
      'Leerling voegt een extra letter toe aan de werkwoordstam (bv. extra "e" of medeklinker).',
    typicalFamilies: ['SWW-LEEN-ENG-STAM'],
  },
  {
    id: 'sww.loanword-double-consonant-lost',
    label: 'Dubbele medeklinker vernederlandst (enkel gemaakt)',
    module: 'SpellingWW',
    description:
      'Leerling maakt een dubbele medeklinker in de leenwoord-stam enkel volgens NL-spelling.',
    typicalFamilies: ['SWW-LEEN-ENG-STAM', 'SWW-LEEN-FRANS'],
  },
  {
    id: 'sww.stem-extra-e-added',
    label: 'Extra -e toegevoegd aan stam',
    module: 'SpellingWW',
    description:
      'Leerling voegt een overbodige -e toe aan de stam (bv. "racee" i.p.v. "race").',
    typicalFamilies: ['SWW-LEEN-ENG-STAM'],
  },
  {
    id: 'sww.infinitive-instead-of-stem',
    label: 'Hele werkwoord gebruikt i.p.v. stam',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt het hele werkwoord (infinitief, -en vorm) op een plek waar de stam hoort.',
    typicalFamilies: ['SWW-LEEN-ENG-STAM', 'SWW-TT-STAM'],
  },
  {
    id: 'sww.noun-instead-of-stem',
    label: 'Zelfstandig naamwoord i.p.v. werkwoordstam',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt een zelfstandig naamwoord (bv. "race") op een plek waar de werkwoordstam ("racet/racete") hoort.',
    typicalFamilies: ['SWW-LEEN-ENG-STAM'],
  },
  {
    id: 'sww.loanword-english-grammar',
    label: 'Engelse grammatica toegepast op leenwoord',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt Engelse uitgangen (bv. -ed) i.p.v. Nederlandse vervoegingsuitgangen bij Engelse leenwoorden.',
    typicalFamilies: ['SWW-LEEN-ENG-STAM', 'SWW-LEEN-ENG-KOF'],
  },
  {
    id: 'sww.loanword-spelling-dutchified',
    label: 'Leenwoord-spelling vernederlandst',
    module: 'SpellingWW',
    description:
      'Leerling past Nederlandse spellingregels toe op de stam van een leenwoord (bv. "save" → "seef", "ç" → "s").',
    typicalFamilies: [
      'SWW-LEEN-ENG-STAM',
      'SWW-LEEN-ENG-KOF',
      'SWW-LEEN-FRANS',
    ],
  },
  {
    id: 'sww.stem-d-lost',
    label: 'Stam-d weggelaten in uitgang',
    module: 'SpellingWW',
    description:
      'Leerling laat de stam-d weg bij vervoeging (bv. "hij race" i.p.v. "hij raced").',
    typicalFamilies: ['SWW-LEEN-ENG-KOF', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.infinitive-after-aux',
    label: 'Infinitief gebruikt na hulpwerkwoord i.p.v. VD',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt het hele werkwoord (infinitief) na een hulpwerkwoord, terwijl het voltooid deelwoord vereist is.',
    typicalFamilies: ['SWW-VD-HULPWW'],
  },
  {
    id: 'sww.noun-verb-confusion',
    label: 'Zelfstandig naamwoord verward met werkwoord',
    module: 'SpellingWW',
    description:
      'Leerling verwart een zelfstandig naamwoord (bv. "belofte") met een verleden-tijdsvorm.',
    typicalFamilies: ['SWW-VT-VZFS'],
  },
  // === Onregelmatige verleden tijd (SWW-VT-ONREGELMATIG) — added mei 2026
  // Unblocks Fase C format-migratie van SWW-VT-ONREGELMATIG Q001/Q003.
  // NL-genaamde tags acceptabel voor Nederlandse werkwoordsanalyse.
  // Let op: sww.onregelmatig-als-regelmatig is semantisch een synoniem van
  // de bestaande sww.irregular-verb-regularized. Bewust beide aangehouden
  // omdat seeds de NL-naam gebruiken; consolidatie kan later (audit K6).
  {
    id: 'sww.onregelmatig-als-regelmatig',
    label: 'Onregelmatig werkwoord regelmatig vervoegd',
    module: 'SpellingWW',
    description:
      'Leerling past de zwakke (kofschip-)regel toe op een onregelmatig werkwoord (bv. "denkde" i.p.v. "dacht", "mogden" i.p.v. "mochten").',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VD-STERK'],
  },
  {
    id: 'sww.tt-vt-verwarring',
    label: 'Tegenwoordige tijd verward met verleden tijd',
    module: 'SpellingWW',
    description:
      'Leerling kiest de tegenwoordige-tijdvorm waar de verleden tijd vereist is (bv. "is" i.p.v. "was").',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VT-STERK', 'SWW-VT-DUBBEL'],
  },
  {
    id: 'sww.zijn-worden-verwarring',
    label: 'Verleden tijd van "zijn" en "worden" verward',
    module: 'SpellingWW',
    description:
      'Leerling kiest "werd" (verleden tijd van worden) waar "was" (verleden tijd van zijn) vereist is, of andersom.',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VT-ZIJN-HEBBEN'],
  },
  {
    id: 'sww.kofschip-op-sterk-ww',
    label: 'Kofschip-regel toegepast op sterk werkwoord',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel toe op een sterk/onregelmatig werkwoord, terwijl de juiste vorm een klankverandering vereist (bv. "denkde" i.p.v. "dacht").',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VT-STERK'],
  },
  {
    id: 'sww.dubbele-uitgang',
    label: 'Dubbele uitgang toegevoegd aan verleden tijd',
    module: 'SpellingWW',
    description:
      'Leerling voegt een overbodige -te of -de toe aan een vorm die al compleet is (bv. "mochtte" i.p.v. "mocht").',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VT-DUBBEL'],
  },
  {
    id: 'sww.regelmatig-als-onregelmatig',
    label: 'Regelmatig werkwoord onterecht als onregelmatig aangemerkt',
    module: 'SpellingWW',
    description:
      'Leerling denkt dat een regelmatig (zwak) werkwoord onregelmatig is, en zoekt naar een klankverandering die er niet hoort (bv. "werken" als onregelmatig zien).',
    typicalFamilies: ['SWW-VT-ONREGELMATIG'],
  },
  {
    id: 'sww.sterk-zwak-verwarring',
    label: 'Sterk en zwak werkwoord verward',
    module: 'SpellingWW',
    description:
      'Leerling kan sterke werkwoorden (klankverandering) niet onderscheiden van zwakke werkwoorden (kofschip-regel) bij categorisatie.',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VT-STERK', 'SWW-VT-ZWAK-KOF'],
  },
  // === Sterk werkwoord voltooid deelwoord (SWW-VD-STERK) — added mei 2026
  // Unblocks pedagogy validator (PEDAGOGY_MISCONCEPTION_UNKNOWN) voor SWW-VD-STERK-001
  // en variants. Sterk VD eindigt altijd op -en; geen kofschip-toepassing toegestaan.
  {
    id: 'sww.vd-sterk-als-zwak',
    label: 'Sterk werkwoord behandeld als zwak (kofschip op VD)',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel toe op een sterk werkwoord in het voltooid deelwoord (bv. "geloopt" i.p.v. "gelopen"), terwijl sterke VD\'s altijd op -en eindigen.',
    typicalFamilies: ['SWW-VD-STERK'],
  },
  {
    id: 'sww.vd-dubbele-klinker',
    label: 'Dubbele klinker in voltooid deelwoord',
    module: 'SpellingWW',
    description:
      'Leerling schrijft een dubbele klinker in het VD waar de open lettergreep enkele klinker vereist (bv. "geloopen" i.p.v. "gelopen").',
    typicalFamilies: ['SWW-VD-STERK', 'SWW-VD-KOF'],
  },
  {
    id: 'sww.vd-verkeerde-uitgang',
    label: 'VD op -d/-t i.p.v. -en bij sterk werkwoord',
    module: 'SpellingWW',
    description:
      'Leerling geeft het voltooid deelwoord van een sterk werkwoord de kofschip-uitgang -d/-t (bv. "gelopd") in plaats van -en.',
    typicalFamilies: ['SWW-VD-STERK'],
  },
  // === SWW seed-driven additions (mei 2026) — Q-IDs in seeds gebruikten deze tags al ===
  {
    id: 'sww.vd-klinker-niet-veranderd',
    label: 'Klinker niet veranderd bij sterk VD',
    module: 'SpellingWW',
    description:
      'Leerling behoudt de oorspronkelijke stamklinker in het voltooid deelwoord i.p.v. de vereiste klankverandering toe te passen (bv. "gedrinkt" i.p.v. "gedronken").',
    typicalFamilies: ['SWW-VD-STERK'],
  },
  // === Inversie jij-vorm (SWW-TT-INV-JIJ) ===
  {
    id: 'sww.inversie-t-niet-weg',
    label: 'Inversie niet toegepast: -t blijft staan',
    module: 'SpellingWW',
    description:
      'Bij inversie (werkwoord vóór "jij") moet de -t weg: "loop jij", niet "loopt jij". Leerling laat de -t per ongeluk staan.',
    typicalFamilies: ['SWW-TT-INV-JIJ'],
  },
  {
    id: 'sww.inversie-t-vergeten',
    label: '-t vergeten bij gewone volgorde',
    module: 'SpellingWW',
    description:
      'Leerling houdt de gewone woordvolgorde aan (jij + werkwoord) maar vergeet daarbij de -t (bv. "jij loop" i.p.v. "jij loopt").',
    typicalFamilies: ['SWW-TT-INV-JIJ'],
  },
  {
    id: 'sww.inversie-infinitief',
    label: 'Infinitief gebruikt bij inversie i.p.v. stam',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt het hele werkwoord (infinitief, -en vorm) bij inversie i.p.v. de stam (bv. "lopen jij" i.p.v. "loop jij").',
    typicalFamilies: ['SWW-TT-INV-JIJ'],
  },
  // === Zijn / hebben oordeel-vraag (SWW-TT-ZIJN-HEBBEN) ===
  {
    id: 'sww.zijnhebben-correct-als-fout',
    label: 'Correcte vorm van zijn/hebben aangezien als fout',
    module: 'SpellingWW',
    description:
      "Leerling beoordeelt een correcte vorm van zijn of hebben (bv. 'jij hebt', 'hij is', 'wij zijn') ten onrechte als foutief in een 'welke zin bevat een fout?'-vraag.",
    typicalFamilies: ['SWW-TT-ZIJN-HEBBEN'],
  },
  // === Gebiedende wijs (SWW-TT-GEBIEDENDE) ===
  {
    id: 'sww.gebiedende-met-t',
    label: 'Gebiedende wijs met -t geschreven',
    module: 'SpellingWW',
    description:
      'Leerling schrijft de gebiedende wijs als jij/hij-vorm met -t (bv. "Wordt!" i.p.v. "Word!"), terwijl de gebiedende wijs altijd de stam zonder -t is.',
    typicalFamilies: ['SWW-TT-GEBIEDENDE'],
  },
  // === Generiek "welke is fout?"-vraagtype ===
  {
    id: 'sww.correct-form-marked-wrong',
    label: 'Correcte vorm aangezien als fout',
    module: 'SpellingWW',
    description:
      "Leerling kiest een correct geschreven vorm als 'fout' in een vraagtype dat vraagt welk antwoord verkeerd geschreven is.",
    typicalFamilies: [
      'SWW-TT-GEBIEDENDE',
      'SWW-TT-ZIJN-HEBBEN',
      'SWW-VD-HOMOFOON',
    ],
  },
  // === Meervoud tegenwoordige tijd (SWW-TT-MEERVOUD) ===
  {
    id: 'sww.meervoud-als-enkelvoud-vervoegd',
    label: 'Meervoudig onderwerp vervoegd als enkelvoud',
    module: 'SpellingWW',
    description:
      "Leerling gebruikt bij een meervoudig onderwerp (wij/jullie/zij/de kinderen) de stam+t-vorm i.p.v. de infinitief (bv. 'de kinderen speelt' of 'wij fietst'). Verward de enkelvoudsregel met de meervoudsregel.",
    typicalFamilies: ['SWW-TT-MEERVOUD'],
  },
  {
    id: 'sww.stam-alleen-bij-meervoud',
    label: 'Stam alleen gebruikt bij meervoud (ik-vorm)',
    module: 'SpellingWW',
    description:
      "Leerling gebruikt bij meervoud de kale stam (ik-vorm) i.p.v. de infinitief (bv. 'wij werk' of 'de vogels vlieg'). Mist het besef dat alleen 'ik' de stam zonder -t/-en gebruikt.",
    typicalFamilies: ['SWW-TT-MEERVOUD'],
  },
  // === Gebiedende wijs herkennen (SWW-TT-GEBIEDENDE) ===
  {
    id: 'sww.gebiedende-niet-herkend',
    label: 'Gebiedende wijs niet herkend als bevel',
    module: 'SpellingWW',
    description:
      'Leerling herkent een bevel/opdracht-zin niet als gebiedende wijs en kiest een gewone mededeling (indicatief), voltooide tijd of meervoudsvorm als zou die ook een bevel zijn.',
    typicalFamilies: ['SWW-TT-GEBIEDENDE'],
  },
  // === Seed-driven additions (mei 2026 — governance triage) ====================
  // The following tags were already in active use by SWW seeds but missing from
  // the registry, causing PEDAGOGY_MISCONCEPTION_UNKNOWN (severity 70) blockers
  // across ~30 questions. Per registry policy (see note at line 142-147), we
  // accept seed-naming style — consolidatie volgt later.

  // --- Leenwoord-kofschip (SWW-LEEN-ENG-KOF) ---
  {
    id: 'sww.kofschip-letter-not-sound',
    label: 'Kofschip toegepast op letter i.p.v. uitspraak-eindklank',
    module: 'SpellingWW',
    description:
      'Leerling kijkt naar de geschreven eindletter van een Engels leenwoord (bv. de stille -e in "race") in plaats van naar de uitgesproken eindklank, en kiest daardoor de verkeerde kofschip-uitgang.',
    typicalFamilies: ['SWW-LEEN-ENG-KOF'],
  },

  // --- VT-vorm verwisselingen (SWW-VT-VZFS, SWW-VT-DUBBEL) ---
  {
    id: 'sww.vf-switch-spelling-error',
    label: 'Letterswitch v↔f in stam bij verleden tijd',
    module: 'SpellingWW',
    description:
      'Leerling verwart v en f in de stam van het werkwoord bij vervoeging (bv. "leefde" vs. "leevde"), terwijl de oorspronkelijke stamletter behouden moet blijven.',
    typicalFamilies: ['SWW-VT-VZFS'],
  },
  {
    id: 'sww.zs-switch-spelling-error',
    label: 'Letterswitch z↔s in stam bij verleden tijd',
    module: 'SpellingWW',
    description:
      'Leerling verwart z en s in de stam van het werkwoord bij vervoeging (bv. "reisde" vs. "reizde"), terwijl de oorspronkelijke stamletter behouden moet blijven.',
    typicalFamilies: ['SWW-VT-VZFS'],
  },
  {
    id: 'sww.kofschip-on-stem-not-infinitive',
    label: 'Kofschip toegepast op infinitief i.p.v. stam',
    module: 'SpellingWW',
    description:
      'Leerling kijkt naar de eindletter van de infinitief (bv. "werken" → -n) in plaats van naar de stam (werk → -k) bij de kofschip-check, en kiest daardoor de verkeerde uitgang.',
    typicalFamilies: ['SWW-VT-VZFS', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.spelling-ei-ij',
    label: 'Spelling-fout ei ↔ ij in werkwoordstam',
    module: 'SpellingWW',
    description:
      'Leerling kiest de verkeerde spelling van de tweeklank /ɛi/ (ei vs. ij) in de stam van het werkwoord bij vervoeging.',
    typicalFamilies: ['SWW-VT-VZFS'],
  },
  {
    id: 'sww.verb-confusion',
    label: 'Verkeerd werkwoord gekozen of vervoegd',
    module: 'SpellingWW',
    description:
      'Leerling kiest een verkeerd basiswerkwoord of vervoegt het verkeerde werkwoord (bv. "werken" i.p.v. "wachten"), waardoor het hele antwoord onjuist wordt.',
    typicalFamilies: ['SWW-VT-VZFS', 'SWW-BN-VD-DUBBEL'],
  },
  {
    id: 'sww.vt-uitgang-t-mist',
    label: 'VT-uitgang -t ontbreekt',
    module: 'SpellingWW',
    description:
      'Leerling laat de -t weg aan het einde van de verleden-tijdvorm (bv. "werke" i.p.v. "werkte"), omdat hij de uitgangsletter vergeet of de stam-t niet verdubbelt.',
    typicalFamilies: ['SWW-VT-DUBBEL', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.vt-uitgang-d-mist',
    label: 'VT-uitgang -d ontbreekt',
    module: 'SpellingWW',
    description:
      'Leerling laat de -d weg aan het einde van de verleden-tijdvorm (bv. "antwoorde" i.p.v. "antwoordde"), omdat hij de stam-d en de uitgangs-d niet allebei schrijft.',
    typicalFamilies: ['SWW-VT-DUBBEL'],
  },
  {
    id: 'sww.vt-uitgang-dubbel',
    label: 'VT-uitgang dubbel geschreven waar enkel hoort',
    module: 'SpellingWW',
    description:
      'Leerling schrijft een dubbele uitgang -tte of -dde waar de stam al op een t of d eindigt en de regel maar één extra letter vereist (bv. "zettete" i.p.v. "zette").',
    typicalFamilies: ['SWW-VT-DUBBEL'],
  },

  // --- VT/TT tijds-verwarringen ---
  {
    id: 'sww.tense-confusion-tt-vt',
    label: 'Tegenwoordige en verleden tijd verward',
    module: 'SpellingWW',
    description:
      'Leerling kiest een tegenwoordige-tijdvorm waar de verleden tijd vereist is (of andersom), zonder het signaalwoord ("gisteren", "vroeger") of de context goed te lezen.',
    typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN', 'SWW-VD-HOMOFOON'],
  },
  {
    id: 'sww.past-participle-wrong-form',
    label: 'Voltooid deelwoord in verkeerde vorm geschreven',
    module: 'SpellingWW',
    description:
      'Leerling schrijft het voltooid deelwoord in een verkeerde vorm — bv. zonder ge-, met verkeerde uitgang, of met de stam i.p.v. de VD-vorm.',
    typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN', 'SWW-VD-HULPWW'],
  },
  {
    id: 'sww.vt-vd-confusion',
    label: 'Verleden tijd en voltooid deelwoord verward',
    module: 'SpellingWW',
    description:
      'Leerling kiest een verleden-tijdvorm (VT: stam + te/de) op een plek waar het voltooid deelwoord (VD: ge + stam + t/d/en) hoort, of andersom.',
    typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN', 'SWW-VD-HULPWW'],
  },
  {
    id: 'sww.vvt-vt-confusion',
    label: 'Voltooid verleden tijd en verleden tijd verward',
    module: 'SpellingWW',
    description:
      'Leerling verwart de voltooid verleden tijd (had + VD) met de gewone verleden tijd (VT), en kiest een vorm met of zonder hulpwerkwoord op de verkeerde plek.',
    typicalFamilies: ['SWW-VD-HOMOFOON'],
  },
  {
    id: 'sww.vd-tt-confusion',
    label: 'Tegenwoordige tijd aangezien voor voltooid deelwoord',
    module: 'SpellingWW',
    description:
      'Leerling herkent een tegenwoordige-tijdvorm (hij fietst) ten onrechte als voltooid deelwoord, omdat hij niet ziet dat een VD altijd bij een hulpwerkwoord (hebben/zijn) staat.',
    typicalFamilies: ['SWW-VD-HULPWW'],
  },
  {
    id: 'sww.future-instead-of-vd',
    label: 'Toekomende tijd (zal + infinitief) i.p.v. voltooid deelwoord',
    module: 'SpellingWW',
    description:
      'Leerling kiest de toekomende-tijdsconstructie "zal + infinitief" op een plek waar de voltooide tijd (hebben/zijn + VD) vereist is.',
    typicalFamilies: ['SWW-VD-HULPWW'],
  },
  {
    id: 'sww.vd-without-aux',
    label: 'Voltooid deelwoord gebruikt zonder hulpwerkwoord',
    module: 'SpellingWW',
    description:
      'Leerling herkent een VD-vorm los van het hulpwerkwoord en kiest hem als zelfstandige werkwoordsvorm, terwijl het VD alleen geldig is samen met hebben/zijn/worden.',
    typicalFamilies: ['SWW-VD-HOMOFOON'],
  },
  {
    id: 'sww.hebben-zijn-wrong',
    label: 'Verkeerde keuze hebben/zijn als hulpwerkwoord',
    module: 'SpellingWW',
    description:
      'Leerling kiest het verkeerde hulpwerkwoord (hebben i.p.v. zijn bij bewegingswerkwoorden, of andersom) in de voltooide tijd.',
    typicalFamilies: ['SWW-VD-HULPWW'],
  },
  {
    id: 'sww.passive-voice-confusion',
    label: 'Lijdende vorm (worden + VD) verward met voltooide tijd',
    module: 'SpellingWW',
    description:
      'Leerling kiest "wordt" (lijdende vorm: worden + VD) als hulpwerkwoord op een plek waar de voltooide tijd (hebben/zijn + VD) vereist is.',
    typicalFamilies: ['SWW-VD-HULPWW'],
  },

  // --- Onregelmatig meervoud + enkelvoud/meervoud-fouten ---
  {
    id: 'sww.irregular-plural-invented',
    label: 'Onregelmatige meervoudsvorm verzonnen',
    module: 'SpellingWW',
    description:
      'Leerling verzint een meervoudsvorm voor een onregelmatig hulpwerkwoord (bv. "zulden" i.p.v. "zullen"), in plaats van de juiste vaste vorm uit het hoofd te gebruiken.',
    typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN'],
  },
  {
    id: 'sww.vt-enkelvoud-bij-meervoud',
    label: 'Enkelvoudige VT-vorm bij meervoudig onderwerp',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt de enkelvoudige verleden-tijdvorm (bv. "kon") bij een meervoudig onderwerp (wij/zij), waar de meervoudsvorm ("konden") vereist is.',
    typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN'],
  },

  // --- Inversie-fouten (SWW-TT-INV-TRICK) ---
  {
    id: 'sww.inversie-t-blijft-staan',
    label: 'Inversie: -t blijft staan bij jij-vorm na werkwoord',
    module: 'SpellingWW',
    description:
      'Bij inversie (werkwoord vóór "jij") moet de -t weg ("loop jij"), maar de leerling laat hem staan ("loopt jij") door de standaardregel "stam + t" te overgeneraliseren.',
    typicalFamilies: ['SWW-TT-INV-TRICK', 'SWW-TT-INV-JIJ'],
  },
  {
    id: 'sww.inversie-d-stam-t-blijft',
    label: 'Inversie bij stam op -d: extra -t blijft staan',
    module: 'SpellingWW',
    description:
      'Bij een werkwoord met stam op -d (bv. "wordt") moet bij inversie alleen de -t weg en de stam-d behouden blijven ("word jij"); leerling laat de -t per ongeluk staan.',
    typicalFamilies: ['SWW-TT-INV-TRICK'],
  },
  {
    id: 'sww.inversie-overgeneraliseren-jij',
    label: 'Inversieregel overgeneraliseerd naar hij/zij',
    module: 'SpellingWW',
    description:
      'Leerling laat ook bij hij/zij de -t weg na inversie ("loop hij" i.p.v. "loopt hij"), omdat hij de jij-inversieregel overgeneraliseert naar alle persoonsvormen.',
    typicalFamilies: ['SWW-TT-INV-TRICK', 'SWW-TT-INV-JIJ'],
  },
  {
    id: 'sww.heel-ww-i.p.v.-stam',
    label: 'Hele werkwoord (infinitief) i.p.v. stam bij inversie',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt het hele werkwoord (infinitief, -en vorm) bij inversie ("lopen jij") in plaats van de stam ("loop jij").',
    typicalFamilies: ['SWW-TT-INV-TRICK'],
  },
  {
    id: 'sww.vt-i.p.v.-tt',
    label: 'Verleden tijd gekozen waar tegenwoordige tijd hoort',
    module: 'SpellingWW',
    description:
      'Leerling kiest een verleden-tijdvorm op een plek waar de tegenwoordige tijd vereist is, omdat hij de context of het signaalwoord verkeerd interpreteert.',
    typicalFamilies: ['SWW-TT-INV-TRICK'],
  },

  // --- VD zonder ge- (SWW-VD-ZONDER-GE) ---
  {
    id: 'sww.vd-ge-toegevoegd',
    label: 'Ge- onterecht toegevoegd aan VD',
    module: 'SpellingWW',
    description:
      'Leerling voegt ge- toe aan een voltooid deelwoord van een werkwoord met onbeklemtoond voorvoegsel (be-/ver-/ont-/her-/er-/ge-), terwijl die werkwoorden juist géén ge- krijgen.',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.vd-tegenwoordige-tijd',
    label: 'Tegenwoordige tijd gekozen i.p.v. voltooid deelwoord',
    module: 'SpellingWW',
    description:
      'Leerling kiest een tegenwoordige-tijdvorm op een plek waar het voltooid deelwoord (na hebben/zijn) vereist is.',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.vd-verleden-tijd',
    label: 'Verleden tijd gekozen i.p.v. voltooid deelwoord',
    module: 'SpellingWW',
    description:
      'Leerling kiest een verleden-tijdvorm op een plek waar het voltooid deelwoord (na hebben/zijn) vereist is — een variant van VT/VD-verwarring.',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.vd-infinitief-gebruikt',
    label: 'Infinitief gebruikt i.p.v. voltooid deelwoord',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt het hele werkwoord (infinitief) na het hulpwerkwoord in plaats van het voltooid deelwoord (bv. "heeft verkopen" i.p.v. "heeft verkocht").',
    typicalFamilies: ['SWW-VD-ZONDER-GE', 'SWW-VD-HULPWW'],
  },
  {
    id: 'sww.vd-bevoheg-niet-herkend',
    label: 'BEVO-HEG-voorvoegsel niet herkend (ge- onterecht toegevoegd)',
    module: 'SpellingWW',
    description:
      'Leerling herkent het onbeklemtoonde voorvoegsel (BEVO-HEG: be-/ver-/ont-/her-/er-/ge-) niet en voegt ten onrechte ge- toe aan het voltooid deelwoord (bv. "gebetaald" i.p.v. "betaald", "geverkocht" i.p.v. "verkocht").',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },

  // --- Bijvoeglijk gebruikt VD (SWW-BN-VD-KORT, SWW-BN-VD-DUBBEL) ---
  {
    id: 'sww.bn-short-vowel-not-doubled',
    label: 'Korte klinker-medeklinker niet verdubbeld bij verbuiging',
    module: 'SpellingWW',
    description:
      'Bij verbuiging van een bijvoeglijk gebruikt VD met korte klinker moet de medeklinker verdubbeld worden ("gestopt" → "gestopte"); leerling laat de verdubbeling weg.',
    typicalFamilies: ['SWW-BN-VD-DUBBEL'],
  },
  {
    id: 'sww.bn-uitgang-en-instead-of-e',
    label: 'Uitgang -en gebruikt waar -e hoort bij verbuiging',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt -en als verbuigings-uitgang van het bijvoeglijk gebruikte VD ("gestoppen man") in plaats van de juiste -e ("gestopte man").',
    typicalFamilies: ['SWW-BN-VD-DUBBEL', 'SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-infinitief-gebruikt',
    label: 'Infinitief gebruikt als bijvoeglijk naamwoord',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt de infinitief (hele werkwoord) als bijvoeglijk naamwoord ("de breken vaas") in plaats van de juiste VD-verbuigingsvorm ("de gebroken vaas").',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-lange-klank-verdubbeld',
    label: 'Lange klinker onterecht verdubbeld bij verbuiging',
    module: 'SpellingWW',
    description:
      'Leerling verdubbelt de klinker bij verbuiging van een bijvoeglijk gebruikt VD met lange klinker ("geslaapen" i.p.v. "geslapen"), terwijl een open lettergreep enkele klinker vereist.',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-ene-toegevoegd',
    label: 'Uitgang -ene toegevoegd i.p.v. -e',
    module: 'SpellingWW',
    description:
      'Leerling voegt -ene toe als verbuigings-uitgang ("gebrokene vaas") in plaats van de juiste -e ("gebroken vaas").',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-en-toegevoegd',
    label: 'Uitgang -en toegevoegd waar geen verbuiging hoort',
    module: 'SpellingWW',
    description:
      'Leerling voegt -en toe aan een bijvoeglijk gebruikt VD waar geen of een andere verbuiging hoort (bv. predicatief gebruik), door de meervoudsregel te overgeneraliseren.',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-kofschip-misapplied',
    label: 'Kofschip-regel verkeerd toegepast op bijvoeglijk VD',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel toe op een bijvoeglijk gebruikt VD-vorm waarbij de regel niet relevant is (de VD-vorm staat al vast).',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-cluster-verdubbeld',
    label: 'Medeklinkercluster onterecht verdubbeld bij verbuiging',
    module: 'SpellingWW',
    description:
      'Leerling verdubbelt een medeklinker in een cluster bij verbuiging (bv. "gewerktte") terwijl de spellingregel dat niet vereist.',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-e-vergeten',
    label: 'Verbuigings-e vergeten bij bijvoeglijk gebruikt VD',
    module: 'SpellingWW',
    description:
      'Leerling vergeet de verbuigings-e toe te voegen aan het bijvoeglijk gebruikte VD ("de gebroken vaas" i.p.v. "de gebroken-e vaas"... eigenlijk "de gebroken vaas" is correct; bedoeld: bij de-woorden met onbepaald lidwoord vergeet hij de -e).',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-sterke-vd-als-fout',
    label: 'Correcte sterke VD-verbuiging aangezien voor fout',
    module: 'SpellingWW',
    description:
      'Leerling kiest een correct verbogen sterk VD (bv. "gelopen man") als foutief antwoord, omdat hij denkt dat de -en uitgang van het sterke VD onverbogen vorm-fout is.',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-sterk-als-zwak',
    label: 'Sterk VD verbogen volgens zwakke regel',
    module: 'SpellingWW',
    description:
      'Leerling past de zwakke VD-verbuiging (-de/-te + e) toe op een sterk VD ("de geloopte man") in plaats van de sterke VD-vorm ("de gelopen man") te gebruiken.',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  {
    id: 'sww.bn-eind-n-weggelaten',
    label: 'Eind-n weggelaten bij sterk VD in verbuiging',
    module: 'SpellingWW',
    description:
      'Leerling laat de eind-n van het sterke VD weg bij verbuiging ("de gelope man" i.p.v. "de gelopen man"), door over te generaliseren vanuit spreektaal.',
    typicalFamilies: ['SWW-BN-VD-KORT'],
  },
  // === Inversie jij-vorm (SWW-TT-INV-JIJ) ===
  {
    id: 'sww.inversie-t-niet-weg',
    label: 'Inversie niet toegepast: -t blijft staan',
    module: 'SpellingWW',
    description:
      'Bij inversie (werkwoord vóór "jij") moet de -t weg: "loop jij", niet "loopt jij". Leerling laat de -t per ongeluk staan.',
    typicalFamilies: ['SWW-TT-INV-JIJ'],
  },
  {
    id: 'sww.inversie-t-vergeten',
    label: '-t vergeten bij gewone volgorde',
    module: 'SpellingWW',
    description:
      'Leerling houdt de gewone woordvolgorde aan (jij + werkwoord) maar vergeet daarbij de -t (bv. "jij loop" i.p.v. "jij loopt").',
    typicalFamilies: ['SWW-TT-INV-JIJ'],
  },
  {
    id: 'sww.inversie-infinitief',
    label: 'Infinitief gebruikt bij inversie i.p.v. stam',
    module: 'SpellingWW',
    description:
      'Leerling gebruikt het hele werkwoord (infinitief, -en vorm) bij inversie i.p.v. de stam (bv. "lopen jij" i.p.v. "loop jij").',
    typicalFamilies: ['SWW-TT-INV-JIJ'],
  },
  // === Zijn / hebben oordeel-vraag (SWW-TT-ZIJN-HEBBEN) ===
  {
    id: 'sww.zijnhebben-correct-als-fout',
    label: 'Correcte vorm van zijn/hebben aangezien als fout',
    module: 'SpellingWW',
    description:
      "Leerling beoordeelt een correcte vorm van zijn of hebben (bv. 'jij hebt', 'hij is', 'wij zijn') ten onrechte als foutief in een 'welke zin bevat een fout?'-vraag.",
    typicalFamilies: ['SWW-TT-ZIJN-HEBBEN'],
  },
  // === Gebiedende wijs (SWW-TT-GEBIEDENDE) ===
  {
    id: 'sww.gebiedende-met-t',
    label: 'Gebiedende wijs met -t geschreven',
    module: 'SpellingWW',
    description:
      'Leerling schrijft de gebiedende wijs als jij/hij-vorm met -t (bv. "Wordt!" i.p.v. "Word!"), terwijl de gebiedende wijs altijd de stam zonder -t is.',
    typicalFamilies: ['SWW-TT-GEBIEDENDE'],
  },
  // === Generiek "welke is fout?"-vraagtype ===
  {
    id: 'sww.correct-form-marked-wrong',
    label: 'Correcte vorm aangezien als fout',
    module: 'SpellingWW',
    description:
      "Leerling kiest een correct geschreven vorm als 'fout' in een vraagtype dat vraagt welk antwoord verkeerd geschreven is.",
    typicalFamilies: [
      'SWW-TT-GEBIEDENDE',
      'SWW-TT-ZIJN-HEBBEN',
      'SWW-VD-HOMOFOON',
    ],
  },
  // === Meervoud tegenwoordige tijd (SWW-TT-MEERVOUD) ===
  {
    id: 'sww.meervoud-als-enkelvoud-vervoegd',
    label: 'Meervoudig onderwerp vervoegd als enkelvoud',
    module: 'SpellingWW',
    description:
      "Leerling gebruikt bij een meervoudig onderwerp (wij/jullie/zij/de kinderen) de stam+t-vorm i.p.v. de infinitief (bv. 'de kinderen speelt' of 'wij fietst'). Verward de enkelvoudsregel met de meervoudsregel.",
    typicalFamilies: ['SWW-TT-MEERVOUD'],
  },
  {
    id: 'sww.stam-alleen-bij-meervoud',
    label: 'Stam alleen gebruikt bij meervoud (ik-vorm)',
    module: 'SpellingWW',
    description:
      "Leerling gebruikt bij meervoud de kale stam (ik-vorm) i.p.v. de infinitief (bv. 'wij werk' of 'de vogels vlieg'). Mist het besef dat alleen 'ik' de stam zonder -t/-en gebruikt.",
    typicalFamilies: ['SWW-TT-MEERVOUD'],
  },
  // === Gebiedende wijs herkennen (SWW-TT-GEBIEDENDE) ===
  {
    id: 'sww.gebiedende-niet-herkend',
    label: 'Gebiedende wijs niet herkend als bevel',
    module: 'SpellingWW',
    description:
      'Leerling herkent een bevel/opdracht-zin niet als gebiedende wijs en kiest een gewone mededeling (indicatief), voltooide tijd of meervoudsvorm als zou die ook een bevel zijn.',
    typicalFamilies: ['SWW-TT-GEBIEDENDE'],
  },
]
